/**
 * Copy da navegação lateral e do command palette.
 *
 * Os rótulos saíram de `lib/nav.ts` e vieram para cá porque `NAV_ITEMS` é uma constante de
 * MÓDULO: escrita ali, a copy congela no idioma do primeiro import e a troca de idioma não a
 * alcança — o menu inteiro continuaria em português. É o mesmo motivo que fez os outros
 * catálogos existirem (ADR-0811).
 *
 * O que ficou em `lib/nav.ts` é só estrutura: rota, ícone, papéis que enxergam o item e a que
 * grupo ele pertence. O `NavGroup` continua com os valores em português porque são
 * IDENTIFICADORES internos, não texto de tela — quem os exibe é `groups` aqui embaixo.
 *
 * As duas tabelas são `Record` fechados: uma rota nova sem rótulo aqui, ou
 * um grupo novo sem nome, reprova a compilação nos dois idiomas em vez de aparecer em branco
 * na barra lateral.
 */
import type { AdminRoutePath, NavGroup } from '../../lib/nav.js';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface NavContent {
  /** Rótulo de cada item, pela rota que ele aponta. */
  /**
   * Rótulo de cada rota. Cobre TODA `AdminRoutePath`, não só as do menu: as sub-páginas
   * (`/plans/new`, `/releases/register`) não aparecem na barra lateral hoje, mas dar-lhes
   * rótulo custa três linhas e torna a tabela exaustiva — rota nova sem rótulo reprova a
   * compilação em vez de aparecer em branco se um dia entrar no menu.
   */
  readonly items: Readonly<Record<AdminRoutePath, string>>;
  /** Nome de cada grupo, como aparece acima do bloco de itens. */
  readonly groups: Readonly<Record<NavGroup, string>>;
}

const NAV_CONTENT: Bilingual<NavContent> = {
  'pt-BR': {
    items: {
      '/dashboard': 'Painel',
      '/metrics': 'Métricas',
      '/downloads': 'Downloads',
      '/companies': 'Empresas',
      '/users': 'Usuários',
      '/plans': 'Planos',
      '/packs': 'Catálogo',
      '/publishers': 'Publishers',
      '/pack-submissions': 'Moderação',
      '/moderation-config': 'Config de moderação',
      '/publisher-guide': 'Guia do Publisher',
      '/support': 'Suporte',
      '/faq': 'FAQ e capacidades',
      '/notifications': 'Notificações',
      '/releases': 'Atualizações',
      '/legal': 'Documentos legais',
      '/staff': 'Equipe',
      '/settings': 'Configurações',
      '/audit': 'Auditoria',
      '/plans/new': 'Novo plano',
      '/plans/new-targeted': 'Novo plano negociado',
      '/releases/register': 'Registrar versão',
    },
    groups: {
      general: 'Geral',
      customers: 'Clientes',
      marketplace: 'Marketplace',
      support: 'Suporte',
      operations: 'Operação',
    },
  },
  'en-US': {
    items: {
      '/dashboard': 'Dashboard',
      '/metrics': 'Metrics',
      '/downloads': 'Downloads',
      '/companies': 'Companies',
      '/users': 'Users',
      '/plans': 'Plans',
      '/packs': 'Catalog',
      /* Termo de produto — não se traduz, e já era usado assim no pt-BR. */
      '/publishers': 'Publishers',
      '/pack-submissions': 'Moderation',
      '/moderation-config': 'Moderation settings',
      '/publisher-guide': 'Publisher guide',
      '/support': 'Support',
      '/faq': 'FAQ and capabilities',
      '/notifications': 'Notifications',
      '/releases': 'Releases',
      '/legal': 'Legal documents',
      '/staff': 'Team',
      '/settings': 'Settings',
      '/audit': 'Audit trail',
      '/plans/new': 'New plan',
      '/plans/new-targeted': 'New negotiated plan',
      '/releases/register': 'Register release',
    },
    groups: {
      general: 'General',
      customers: 'Customers',
      marketplace: 'Marketplace',
      support: 'Support',
      operations: 'Operations',
    },
  },
};

export const useNavContent = (): NavContent => useBilingual(NAV_CONTENT);

/** Exportado para teste: as duas tabelas precisam cobrir toda rota e todo grupo. */
export { NAV_CONTENT };
